import React, { useState, useEffect } from "react";
import { Button, Spin, Result } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import QRCode from "qrcode";
import axios from "axios";
import { API_BASE_URL, FRONTEND_BASE_URL } from "../../../config/api";
import { getStateInfoFromGstin } from "../../../utils/gstUtils";
import BottomNavigation from "../bottom-navigation/BottomNavigation";
import "./invoice.css";

function numberToWordsIndian(num) {
  if (!num || Number.isNaN(Number(num))) return "Rupees Zero Only";
  const a = ["", "One ", "Two ", "Three ", "Four ", "Five ", "Six ", "Seven ", "Eight ", "Nine ", "Ten ", "Eleven ", "Twelve ", "Thirteen ", "Fourteen ", "Fifteen ", "Sixteen ", "Seventeen ", "Eighteen ", "Nineteen "];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  function inWords(val) {
    const nStr = String(val);
    if (nStr.length > 9) return "overflow";
    const nArr = /^(\d{2})(\d{2})(\d{2})(\d)(\d{2})$/.exec(("000000000" + nStr).slice(-9));
    if (!nArr) return "";
    let str = "";
    if (Number(nArr[1]) !== 0) {
      str += (a[Number(nArr[1])] || `${b[nArr[1][0]]} ${a[nArr[1][1]]}`) + "Crore ";
    }
    if (Number(nArr[2]) !== 0) {
      str += (a[Number(nArr[2])] || `${b[nArr[2][0]]} ${a[nArr[2][1]]}`) + "Lakh ";
    }
    if (Number(nArr[3]) !== 0) {
      str += (a[Number(nArr[3])] || `${b[nArr[3][0]]} ${a[nArr[3][1]]}`) + "Thousand ";
    }
    if (Number(nArr[4]) !== 0) {
      str += (a[Number(nArr[4])] || `${b[nArr[4][0]]} ${a[nArr[4][1]]}`) + "Hundred ";
    }
    const tensVal = Number(nArr[5]);
    if (tensVal !== 0) {
      const prefix = str !== "" ? "and " : "";
      const word = a[tensVal] || `${b[nArr[5][0]]} ${a[nArr[5][1]]}`;
      str += `${prefix}${word}`;
    }
    return str.trim();
  }

  const rounded = Math.round(Number(num));
  const words = inWords(rounded);
  return words ? `Rupees ${words} Only` : "Rupees Zero Only";
}

const resolveAssetUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://") || trimmed.startsWith("data:")) {
    return trimmed;
  }
  if (trimmed.startsWith("/")) {
    return trimmed;
  }
  return `${API_BASE_URL}/${trimmed}`;
};

function PublicInvoiceView({ token }) {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [logoLoadError, setLogoLoadError] = useState(false);
  const [sealLoadError, setSealLoadError] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("No invoice token specified.");
      setLoading(false);
      return;
    }

    const fetchPublicInvoice = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE_URL}/api/invoices/public/${encodeURIComponent(token)}`);
        if (res.data?.success && res.data?.invoice) {
          const inv = res.data.invoice;
          setInvoice(inv);

          // Generate QR code for this public invoice URL
          const publicLink = `${FRONTEND_BASE_URL}/invoice/${encodeURIComponent(token)}`;
          try {
            const qr = await QRCode.toDataURL(publicLink, {
              width: 140,
              margin: 1,
              color: {
                dark: "#0f172a",
                light: "#ffffff",
              },
            });
            setQrCodeUrl(qr);
          } catch (qrErr) {
            console.error("QR Code error:", qrErr);
          }
        } else {
          setError(res.data?.message || "Invoice not found or expired.");
        }
      } catch (err) {
        console.error("Failed to load public invoice:", err);
        setError("Invoice not found or expired.");
      } finally {
        setLoading(false);
      }
    };

    fetchPublicInvoice();
  }, [token]);

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f1f5f9" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f1f5f9", padding: 20 }}>
        <Result status="404" title="Invoice Not Found" subTitle={error || "The invoice you are trying to view does not exist or has expired."} />
      </div>
    );
  }

  const comp = invoice.companySnapshot || {};
  const client = invoice.clientSnapshot || {};
  const items = Array.isArray(invoice.items) ? invoice.items : [];
  const displayCompanyName = comp.name || "Softrate Technologies Private Limited";
  const gstRate = Number(invoice.gstPercentage) || 18;
  const halfRate = gstRate / 2;

  const compGst = comp.gstNumber || "";
  const derivedCompInfo = compGst ? getStateInfoFromGstin(compGst) : { stateCode: "", stateName: "" };
  const compStateName = comp.stateName || comp.state || derivedCompInfo.stateName || "";
  const compStateCode = comp.stateCode || derivedCompInfo.stateCode || "";

  const clientGst = client.gstNumber || "";
  const hasCustomerGst = Boolean(clientGst && String(clientGst).trim() && String(clientGst).trim() !== "—" && String(clientGst).trim() !== "-");
  const derivedClientInfo = clientGst ? getStateInfoFromGstin(clientGst) : { stateCode: "", stateName: "" };
  const clientStateName = client.stateName || client.state || derivedClientInfo.stateName || "";
  const clientStateCode = client.stateCode || derivedClientInfo.stateCode || "";

  const isInterState = Boolean(
    invoice.isInterState ||
    (Number(invoice.igst || 0) > 0 && !Number(invoice.cgst || 0) && !Number(invoice.sgst || 0)) ||
    (compStateCode && clientStateCode && compStateCode !== clientStateCode)
  );

  // Grouped HSN/SAC GST Breakdown
  const gstBreakdownMap = new Map();
  items.forEach((it) => {
    const hsn = it.sacHsn || "998313";
    const taxable = Number(it.taxable || (Number(it.quantity || 1) * Number(it.rate || 0)));

    if (isInterState) {
      const igst = Number(it.igst || (taxable * gstRate) / 100);
      const totalTax = igst;

      if (gstBreakdownMap.has(hsn)) {
        const existing = gstBreakdownMap.get(hsn);
        existing.taxableValue += taxable;
        existing.igstAmount += igst;
        existing.totalTax += totalTax;
      } else {
        gstBreakdownMap.set(hsn, {
          hsnSac: hsn,
          taxableValue: taxable,
          igstRate: gstRate,
          igstAmount: igst,
          totalTax,
          isInterState: true,
        });
      }
    } else {
      const cgst = Number(it.cgst || (taxable * halfRate) / 100);
      const sgst = Number(it.sgst || (taxable * halfRate) / 100);
      const totalTax = cgst + sgst;

      if (gstBreakdownMap.has(hsn)) {
        const existing = gstBreakdownMap.get(hsn);
        existing.taxableValue += taxable;
        existing.cgstAmount += cgst;
        existing.sgstAmount += sgst;
        existing.totalTax += totalTax;
      } else {
        gstBreakdownMap.set(hsn, {
          hsnSac: hsn,
          taxableValue: taxable,
          cgstRate: halfRate,
          cgstAmount: cgst,
          sgstRate: halfRate,
          sgstAmount: sgst,
          totalTax,
          isInterState: false,
        });
      }
    }
  });
  const gstBreakdown = Array.from(gstBreakdownMap.values());

  const rawTerms = comp.terms || "";
  const termsList = Array.isArray(rawTerms) ? rawTerms : rawTerms.split("\n").map((t) => t.trim()).filter(Boolean);

  return (
    <div style={{ minHeight: "100vh", background: "#cbd5e1", padding: "24px 16px", display: "flex", flexDirection: "column", alignItems: "center" }}>
      {/* Top Floating Action Bar */}
      <div style={{ width: "min(100%, 794px)", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>Tax Invoice: {invoice.invoiceNumber}</span>
        <Button type="primary" icon={<PrinterOutlined />} onClick={() => window.print()}>
          Print / Download PDF
        </Button>
      </div>

      {/* EXACT SOFTRATE WORKSPACE TAX INVOICE PREVIEW (#invoice-preview) */}
      <div id="invoice-preview" className="invoice-preview">
        {/* 1. Tax Invoice Bar */}
        <div className="tax-invoice-bar">
          <span className="invoice-title-main">TAX INVOICE</span>
          <span className="invoice-title-sub">ORIGINAL FOR RECIPIENT</span>
        </div>

        {/* 2. Grid Header */}
        <div className="invoice-grid-header">
          <div className="invoice-grid-col-left">
            <div className="sender-info-block">
              <div className="sender-logo-wrap">
                {resolveAssetUrl(comp.logo) && !logoLoadError ? (
                  <img src={resolveAssetUrl(comp.logo)} alt="Invoice logo" onError={() => setLogoLoadError(true)} className="sender-logo-img" />
                ) : (
                  <div className="sender-text-logo">{displayCompanyName.slice(0, 8)}</div>
                )}
              </div>
              <div className="sender-details">
                <div className="company-title">{displayCompanyName}</div>
                {compGst && (
                  <p className="company-gst">
                    GSTIN: <strong>{compGst}</strong>
                    {(compStateName || compStateCode) && (
                      <>
                        {" | "}State: <strong>{compStateName || "—"}</strong>
                        {compStateCode && <> (Code: <strong>{compStateCode}</strong>)</>}
                      </>
                    )}
                  </p>
                )}
                {!compGst && (compStateName || compStateCode) && (
                  <p className="company-gst">
                    State: <strong>{compStateName || "—"}</strong>
                    {compStateCode && <> (Code: <strong>{compStateCode}</strong>)</>}
                  </p>
                )}
                <p className="company-addr">{comp.registeredAddress || "No. 60A, Softrate Tech Park, Velleeswaran Kovil Street, Chennai 600 122"}</p>
                <p className="company-contact">
                  <span>Mobile: {comp.phone || "+91 8148633580"}</span>
                  <span> | </span>
                  <span>Email: {comp.email || "helpdesk@softrateglobal.com"}</span>
                </p>
              </div>
            </div>

            <div className="client-info-block">
              <div className="block-kicker">Customer Details:</div>
              <strong className="client-company-title">{(invoice.leadCompanyName || client.companyName || "CLIENT COMPANY").toUpperCase()}</strong>
              <div className="client-billing-address">
                <strong>Billing address:</strong>
                <p>{client.address || "—"}</p>
                {(invoice.contactNumber || client.phone) && <p className="client-phone">Ph: {invoice.contactNumber || client.phone}</p>}
                {(invoice.directorEmailAddress || client.email) && <p style={{ margin: 0 }}>Email: {invoice.directorEmailAddress || client.email}</p>}
                {clientGst && (
                  <p style={{ margin: "2px 0 0" }}>
                    GSTIN: <strong>{clientGst}</strong>
                    {(clientStateName || clientStateCode) && (
                      <>
                        {" | "}State: <strong>{clientStateName || "—"}</strong>
                        {clientStateCode && <> (Code: <strong>{clientStateCode}</strong>)</>}
                      </>
                    )}
                  </p>
                )}
                {!clientGst && (clientStateName || clientStateCode) && (
                  <p style={{ margin: "2px 0 0" }}>
                    State: <strong>{clientStateName || "—"}</strong>
                    {clientStateCode && <> (Code: <strong>{clientStateCode}</strong>)</>}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="invoice-grid-col-right">
            <div className="meta-field-row">
              <div className="meta-field-cell">
                <span className="field-label">Invoice #:</span>
                <strong className="field-val">{invoice.invoiceNumber}</strong>
              </div>
              <div className="meta-field-cell">
                <span className="field-label">Invoice Date:</span>
                <strong className="field-val">{dayjs(invoice.invoiceDate || invoice.createdAt).format("DD MMM YYYY")}</strong>
              </div>
            </div>
            <div className="meta-field-full-row">
              <span className="field-label invoice-qr-label">Scan to View E-Invoice</span>
              <div className="invoice-qr-wrap">
                {qrCodeUrl ? <img src={qrCodeUrl} alt="E-Invoice QR Code" className="invoice-qr-image" /> : <div className="invoice-qr-placeholder-box">QR</div>}
              </div>
            </div>
            <div className="meta-filler-space meta-status-space">
              <div className="invoice-status-line">
                <span className="field-label">Invoice Status:</span>
                <strong
                  className={`invoice-status-value ${
                    String(invoice.paymentStatus).toLowerCase() === "paid"
                      ? "invoice-status-value-paid"
                      : String(invoice.paymentStatus).toLowerCase() === "partially paid" || (Number(invoice.amountPaid || 0) > 0 && Number(invoice.balanceDue || 0) > 0)
                      ? "invoice-status-value-partial"
                      : ""
                  }`}
                >
                  {String(invoice.paymentStatus).toLowerCase() === "paid"
                    ? "PAID"
                    : String(invoice.paymentStatus).toLowerCase() === "partially paid" || (Number(invoice.amountPaid || 0) > 0 && Number(invoice.balanceDue || 0) > 0)
                    ? "PARTIALLY PAID"
                    : "UNPAID"}
                </strong>
              </div>
              {invoice.paymentMethod && (
                <div className="invoice-status-line" style={{ marginTop: 4 }}>
                  <span className="field-label">Payment Method:</span>
                  <strong style={{ fontSize: "0.82rem", color: "#1e293b" }}>
                    {invoice.paymentMethod}
                  </strong>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 3. Items Table */}
        <div className="invoice-main-table-wrap">
          <table className="invoice-main-table">
            <thead>
              <tr>
                <th className="col-sno text-center">#</th>
                <th className="col-desc">Item</th>
                <th className="col-hsn text-center">HSN/SAC</th>
                <th className="col-rate text-right">Rate/ Item</th>
                <th className="col-qty text-center">Qty</th>
                <th className="col-taxable text-right">Taxable Value</th>
                <th className="col-tax text-right">Tax Amount</th>
                <th className="col-amount text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => {
                const qty = Number(item.quantity) || 1;
                const rate = Number(item.rate) || 0;
                const taxable = Number(item.taxable) || (qty * rate);
                const taxAmount = Number(item.cgst || 0) + Number(item.sgst || 0) || ((taxable * gstRate) / 100);
                const total = Number(item.total) || (taxable + taxAmount);

                return (
                  <tr key={item.id || item._id || `${item.name || "item"}-${i}`} className="item-row">
                    <td className="text-center">{i + 1}</td>
                    <td>
                      <div className="item-main-name">
                        <strong>{item.name || "Service / Product"}</strong>
                      </div>
                    </td>
                    <td className="text-center">{item.sacHsn || "—"}</td>
                    <td className="text-right">₹{rate.toLocaleString()}</td>
                    <td className="text-center">{qty}</td>
                    <td className="text-right">₹{taxable.toLocaleString()}</td>
                    <td className="text-right">
                      {gstRate > 0 ? (
                        <>
                          ₹{taxAmount.toFixed(2)}
                          <div className="item-tax-pct">({gstRate}%)</div>
                        </>
                      ) : (
                        <span style={{ fontSize: 10, color: "#64748b" }}>Exempt</span>
                      )}
                    </td>
                    <td className="text-right">
                      <strong>₹{total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                    </td>
                  </tr>
                );
              })}

              {/* Table Summary Row */}
              <tr className="table-summary-row">
                <td colSpan={5} className="summary-left-cell">
                  Total Items / Qty : {items.length} / {items.reduce((acc, it) => acc + (Number(it.quantity) || 1), 0)}.000
                </td>
                <td colSpan={3} className="summary-right-cell">
                  {invoice.paymentStatus !== "unpaid" && (
                    <>
                      <div className="summary-line">
                        <span>Taxable Amount</span>
                        <strong>₹{Number(invoice.subtotal || 0).toLocaleString()}</strong>
                      </div>
                      {isInterState ? (
                        <div className="summary-line">
                          <span>IGST ({gstRate}%)</span>
                          <strong>₹{Number(invoice.igst || invoice.gstAmount || ((invoice.subtotal || 0) * gstRate) / 100).toFixed(2)}</strong>
                        </div>
                      ) : (
                        <>
                          <div className="summary-line">
                            <span>CGST ({halfRate}%)</span>
                            <strong>₹{Number(invoice.cgst || ((invoice.subtotal || 0) * halfRate) / 100).toFixed(2)}</strong>
                          </div>
                          <div className="summary-line">
                            <span>SGST ({halfRate}%)</span>
                            <strong>₹{Number(invoice.sgst || ((invoice.subtotal || 0) * halfRate) / 100).toFixed(2)}</strong>
                          </div>
                        </>
                      )}
                    </>
                  )}
                  <div className="summary-line grand-total-line">
                    <span>Total</span>
                    <strong className="grand-total-val">₹{Number(invoice.total || 0).toLocaleString()}</strong>
                  </div>
                  {invoice.paymentStatus === "paid" && (
                    <div className="summary-line">
                      <span>Amount Paid</span>
                      <strong>₹{Number(invoice.amountPaid || invoice.total || 0).toLocaleString()}</strong>
                    </div>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* 4. Footer Stack */}
        <div className="invoice-footer-stack">
          <div className="total-in-words-row">
            Total amount (in words): <strong>{numberToWordsIndian(invoice.total || 0)}</strong>
          </div>

          {/* GST Breakdown Table - Adaptive for Inter-State (IGST) vs Intra-State (CGST + SGST) */}
          {hasCustomerGst && gstRate > 0 && gstBreakdown.length > 0 && (
            <div className="gst-breakdown-section">
              {isInterState ? (
                <table className="gst-breakdown-table">
                  <thead>
                    <tr>
                      <th rowSpan={2} className="text-center" style={{ verticalAlign: "middle" }}>HSN/SAC</th>
                      <th rowSpan={2} className="text-right" style={{ verticalAlign: "middle" }}>TAXABLE VALUE</th>
                      <th colSpan={2} className="text-center border-b">INTEGRATED TAX</th>
                      <th rowSpan={2} className="text-right" style={{ verticalAlign: "middle" }}>TOTAL TAX AMOUNT</th>
                    </tr>
                    <tr>
                      <th className="text-center">RATE</th>
                      <th className="text-right">AMOUNT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gstBreakdown.map((row, idx) => (
                      <tr key={row.hsnSac ? `${row.hsnSac}-${row.igstRate}-${idx}` : `gst-${idx}`}>
                        <td className="text-center">{row.hsnSac}</td>
                        <td className="text-right">INR {row.taxableValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="text-center">{row.igstRate}%</td>
                        <td className="text-right">INR {row.igstAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="text-right"><strong>INR {row.totalTax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
                      </tr>
                    ))}
                    <tr className="gst-breakdown-total-row">
                      <td className="text-center"><strong>TOTAL</strong></td>
                      <td className="text-right"><strong>INR {Number(invoice.subtotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
                      <td className="text-center">—</td>
                      <td className="text-right"><strong>INR {Number(invoice.igst || invoice.gstAmount || ((invoice.subtotal || 0) * gstRate) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
                      <td className="text-right"><strong>INR {Number(invoice.gstAmount || ((invoice.subtotal || 0) * gstRate) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <table className="gst-breakdown-table">
                  <thead>
                    <tr>
                      <th rowSpan={2} className="text-center" style={{ verticalAlign: "middle" }}>HSN/SAC</th>
                      <th rowSpan={2} className="text-right" style={{ verticalAlign: "middle" }}>TAXABLE VALUE</th>
                      <th colSpan={2} className="text-center border-b">CENTRAL TAX</th>
                      <th colSpan={2} className="text-center border-b">STATE TAX</th>
                      <th rowSpan={2} className="text-right" style={{ verticalAlign: "middle" }}>TOTAL TAX AMOUNT</th>
                    </tr>
                    <tr>
                      <th className="text-center">RATE</th>
                      <th className="text-right">AMOUNT</th>
                      <th className="text-center">RATE</th>
                      <th className="text-right">AMOUNT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gstBreakdown.map((row, idx) => (
                      <tr key={row.hsnSac ? `${row.hsnSac}-${row.cgstRate}-${idx}` : `gst-${idx}`}>
                        <td className="text-center">{row.hsnSac}</td>
                        <td className="text-right">INR {row.taxableValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="text-center">{row.cgstRate}%</td>
                        <td className="text-right">INR {row.cgstAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="text-center">{row.sgstRate}%</td>
                        <td className="text-right">INR {row.sgstAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="text-right"><strong>INR {row.totalTax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
                      </tr>
                    ))}
                    <tr className="gst-breakdown-total-row">
                      <td className="text-center"><strong>TOTAL</strong></td>
                      <td className="text-right"><strong>INR {Number(invoice.subtotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
                      <td className="text-center">—</td>
                      <td className="text-right"><strong>INR {Number(invoice.cgst || ((invoice.subtotal || 0) * halfRate) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
                      <td className="text-center">—</td>
                      <td className="text-right"><strong>INR {Number(invoice.sgst || ((invoice.subtotal || 0) * halfRate) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
                      <td className="text-right"><strong>INR {Number(invoice.gstAmount || ((invoice.subtotal || 0) * gstRate) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          )}

          <div className="invoice-grid-footer">
            <div className="footer-col-bank">
              <div className="footer-kicker">Bank Details:</div>
              <dl className="bank-details-list">
                <dt>Bank:</dt>
                <dd>{comp.bankDetails?.bankName || "YES BANK"}</dd>
                <dt>Account #:</dt>
                <dd>{comp.bankDetails?.accountNumber || "66789999222445"}</dd>
                <dt>IFSC:</dt>
                <dd>{comp.bankDetails?.ifscCode || "YESBBIN4567"}</dd>
                <dt>Branch:</dt>
                <dd>{comp.bankDetails?.branchName || "Kodihalli"}</dd>
              </dl>
            </div>

            <div className="footer-col-sig">
              <span className="sig-header">For {displayCompanyName.toUpperCase()}</span>
              <div className="sig-stamp-container">
                {resolveAssetUrl(comp.seal) && !sealLoadError ? (
                  <img src={resolveAssetUrl(comp.seal)} alt="Company Seal" onError={() => setSealLoadError(true)} className="signature-stamp-img" />
                ) : (
                  <svg viewBox="0 0 120 120" width="85" height="85" className="signature-stamp">
                    <circle cx="60" cy="60" r="50" fill="none" stroke="#2563eb" strokeWidth="1.5" strokeDasharray="3 3" />
                    <circle cx="60" cy="60" r="45" fill="none" stroke="#2563eb" strokeWidth="1.2" />
                    <text fill="#2563eb" fontSize="7.5" fontFamily="monospace" letterSpacing="1" textAnchor="middle" x="60" y="24">
                      ★ SIGNATURE ★
                    </text>
                    <text fill="#2563eb" fontSize="7" fontFamily="monospace" letterSpacing="1" textAnchor="middle" x="60" y="104">
                      OFFICIAL SEAL
                    </text>
                    <g transform="rotate(-15 60 60)">
                      <rect x="20" y="47" width="80" height="26" rx="3" fill="#ffffff" fillOpacity="0.9" stroke="#2563eb" strokeWidth="1.2" />
                      <text x="60" y="61" fill="#2563eb" fontSize="8.5" fontWeight="bold" textAnchor="middle">
                        {displayCompanyName.slice(0, 15).toUpperCase()}
                      </text>
                      <text x="60" y="70" fill="#2563eb" fontSize="6.5" textAnchor="middle">
                        VERIFIED
                      </text>
                    </g>
                  </svg>
                )}
              </div>
              <span className="sig-footer">Authorized Signatory (Digital Signature)</span>
            </div>
          </div>

          <div className="invoice-notes-terms-grid">
            <div className="notes-block">
              <div className="notes-kicker">Notes:</div>
              <p>Thank you for the Business</p>
            </div>
            {termsList.length > 0 && (
              <div className="terms-block">
                <div className="terms-kicker">Terms and Conditions:</div>
                <ol>
                  {termsList.map((t, idx) => (
                    <li key={`term-${t.slice(0, 15)}-${idx}`}>{t}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>

          <div className="invoice-bottom-page-footer">
            <span>Page 1/1</span>
            <span>This is a digitally signed document.</span>
          </div>
        </div>
      </div>
      <BottomNavigation />
    </div>
  );
}

export default PublicInvoiceView;
